// Boot-time seeding of the stored webforms admin config (app_store key
// `ppp-webforms-v1`).
//
// WHY THIS EXISTS AT ALL
// ----------------------
// `ppp-webforms-v1` is an operator-edited document that long pre-dates several
// of the forms the app now ships. A config saved before the cattle module, the
// sheep module, the weigh-in rework or the Add Feed form simply has no entry
// for them — and `webformsConfig.webforms` is what WebformsAdminView lists for
// editing, what `configuredFields()` validates against, and what the
// maintenance coordinator publishes as the public `full_config` mirror that
// anonymous webforms read. A missing definition is therefore not cosmetic: the
// form disappears from the admin editor and from the anon mirror.
//
// So each definition below is injected ONLY when the stored document does not
// already carry that id. A stored definition always wins, however far it has
// diverged from the default — operator edits are the source of truth, and this
// module must never overwrite them.
//
// WHY IT IS NOT A DATABASE BACKFILL
// ---------------------------------
// This is a merge that runs against whatever document the environment actually
// holds, not a one-time bootstrap. Production's stored document already carries
// all four ids (so every branch here is currently inert there), but the e2e
// scenario seeds in tests/scenarios/*_seed.js deliberately write partial
// documents, and a restored or hand-edited document can be partial too. A
// backfill can only fix the rows that exist when it runs; this fixes whatever
// is loaded, every boot.
//
// EXTRACTED, NOT REWRITTEN
// ------------------------
// This is a verbatim behavioural extraction of the 363-line block that used to
// sit inline in loadAllDataBody (src/App.tsx). Three of the four definitions
// were a second, hand-maintained copy of entries that already lived in
// DEFAULT_WEBFORMS_CONFIG; they are now READ from there, so the two copies can
// no longer drift. Equivalence with the pre-extraction block is pinned
// case-by-case by tests/fixtures/webform-boot-seed-baseline.json, which was
// generated by running the original inline code as a black box.

import {DEFAULT_WEBFORMS_CONFIG} from '../../lib/defaults.js';
import type {WebformConfig} from '../../lib/defaults.js';
import type {WebformDefinition, WebformsConfig} from '../maintenance/webformMirrorPayloads.js';

// The one seed with no counterpart in DEFAULT_WEBFORMS_CONFIG. Kept verbatim
// from the inline block rather than added to the defaults blob: adding it there
// would also change the pre-boot default config, which is a separate decision
// with its own blast radius (see the report accompanying this extraction).
const ADD_FEED_WEBFORM: WebformConfig = {
  id: 'add-feed-webform',
  teamMembers: [],
  name: 'Add Feed Webform',
  description: 'Quick feed logging for pig, broiler, and layer programs',
  table: 'multiple',
  allowAddGroup: false,
  sections: [
    {
      id: 's-info',
      title: 'Report Info',
      system: true,
      fields: [
        {id: 'date', label: 'Date', type: 'date', required: true, system: true, enabled: true},
        {
          id: 'team_member',
          label: 'Team Member',
          type: 'team_picker',
          required: false,
          system: false,
          enabled: true,
        },
      ],
    },
    {
      id: 's-feed',
      title: 'Feed',
      system: false,
      fields: [
        {
          id: 'batch_label',
          label: 'Batch / Group',
          type: 'group_picker',
          required: true,
          system: true,
          enabled: true,
        },
        {
          id: 'feed_type',
          label: 'Feed Type',
          type: 'button_toggle',
          options: ['STARTER', 'GROWER', 'LAYER'],
          required: true,
          system: false,
          enabled: true,
        },
        {
          id: 'feed_lbs',
          label: 'Feed (lbs)',
          type: 'number',
          required: true,
          system: false,
          enabled: true,
        },
      ],
    },
  ],
};

// Reads a seed definition out of the shared defaults blob BY NAME, and fails
// loudly if it is not there. Silently skipping a missing default would turn a
// renamed or deleted webform into a form that quietly stops appearing in the
// admin editor and in the anon mirror — the exact failure this module exists to
// prevent. webformsConfigSeed.test.ts pins the id list independently, so a
// rename fails in tests before it can ever fail at boot.
function fromDefaults(id: string): WebformConfig {
  const found = DEFAULT_WEBFORMS_CONFIG.webforms.find((w) => w.id === id);
  if (!found) {
    throw new Error(
      `webformsConfigSeed: DEFAULT_WEBFORMS_CONFIG has no '${id}' definition. ` +
        'A boot seed must not silently disappear — either restore the default or ' +
        'remove the id from WEBFORM_BOOT_SEED_IDS deliberately.',
    );
  }
  return found;
}

// Injection order is the original inline order. It is observable: seeds are
// APPENDED, so it determines the tail order of `webforms` on a partial config,
// and that array is mirrored verbatim into `full_config.webforms`.
export const WEBFORM_BOOT_SEED_IDS: readonly string[] = Object.freeze([
  'add-feed-webform',
  'cattle-dailys',
  'sheep-dailys',
  'weighins-webform',
]);

const SEEDS: readonly WebformConfig[] = Object.freeze([
  ADD_FEED_WEBFORM,
  fromDefaults('cattle-dailys'),
  fromDefaults('sheep-dailys'),
  fromDefaults('weighins-webform'),
]);

// Cattle daily mortality moved to the cow record; the section is stripped from
// any previously-saved config so the admin editor never offers it again. The
// public mirror applies the same strip independently
// (webformMirrorPayloads.normalizeWebforms) because that module is a pure
// derivation with no boot dependency — the duplication is deliberate, and each
// copy guards a different consumer (React state here, the mirror there).
function stripCattleMortality(webforms: WebformDefinition[]): WebformDefinition[] {
  return webforms.map(function (w) {
    if (w.id !== 'cattle-dailys') return w;
    return {
      ...w,
      sections: (w.sections || []).filter(function (s) {
        return s.id !== 's-mortality';
      }),
    };
  });
}

/**
 * Returns the stored config with any missing seed definition appended and the
 * legacy cattle `s-mortality` section stripped. Never mutates `stored`.
 *
 * Each injected definition is deep-copied, so a caller that mutates the result
 * cannot reach back into DEFAULT_WEBFORMS_CONFIG — the inline block it replaces
 * built a fresh object literal on every boot, and that property is preserved.
 */
export function applyWebformsConfigSeeds(stored: WebformsConfig): WebformsConfig {
  let cfg: WebformsConfig = stored;
  for (const seed of SEEDS) {
    const existing = cfg.webforms || [];
    if (
      existing.find(function (w) {
        return w.id === seed.id;
      })
    ) {
      continue;
    }
    cfg = {...cfg, webforms: [...existing, structuredClone(seed) as WebformDefinition]};
  }
  return {...cfg, webforms: stripCattleMortality(cfg.webforms || [])};
}
